/**
 * Fixgo Theme — product.js
 * Chargé uniquement sur la fiche produit.
 *  - Galerie : miniatures, flèches, synchronisation au défilement, loupe, plein écran
 *  - Sélecteur de variantes : prix, disponibilité, stock, badges, URL, média associé
 *  - Barre d'ajout au panier collante (mobile)
 */
(() => {
  'use strict';

  const theme = window.theme || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ProductSection {
    constructor(root) {
      this.root = root;
      const json = root.querySelector('[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.select = root.querySelector('[data-variant-select]');
      this.picker = root.querySelector('[data-variant-picker]');
      this.current = 0;

      this.initGallery();
      this.initPicker();
      this.initSticky();
    }

    /* ---------------- Galerie ---------------- */
    initGallery() {
      const gallery = this.root.querySelector('[data-gallery]');
      if (!gallery) return;
      this.slidesEl = gallery.querySelector('[data-gallery-slides]');
      this.slides = Array.from(this.slidesEl.children);
      this.thumbs = Array.from(gallery.querySelectorAll('[data-thumb-index]'));

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex)))
      );
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.current - 1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.current + 1));

      // Met à jour la miniature active quand on fait défiler au doigt
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => entries.forEach((en) => {
            if (en.isIntersecting) this.setActive(Number(en.target.dataset.index));
          }),
          { root: this.slidesEl, threshold: 0.6 }
        );
        this.slides.forEach((s) => io.observe(s));
      }

      // Loupe au survol (souris uniquement) + plein écran au clic
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const zoomEnabled = this.root.dataset.zoom !== 'false';
      gallery.querySelectorAll('[data-zoom-trigger]').forEach((btn) => {
        const img = btn.querySelector('img');
        if (zoomEnabled && finePointer && img) {
          btn.addEventListener('mousemove', (e) => {
            const r = btn.getBoundingClientRect();
            img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
            btn.classList.add('is-zoomed');
          });
          btn.addEventListener('mouseleave', () => btn.classList.remove('is-zoomed'));
        }
        btn.addEventListener('click', () => this.openLightbox(btn.dataset.zoomSrc, img ? img.alt : ''));
      });

      // Visionneuse plein écran (<dialog> natif : focus et Échap gérés par le navigateur)
      this.lightbox = gallery.querySelector('[data-lightbox]');
      if (this.lightbox) {
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('click', (e) => { if (e.target === this.lightbox) this.lightbox.close(); });
      }
    }

    goTo(index) {
      if (!this.slides || !this.slides.length) return;
      const i = (index + this.slides.length) % this.slides.length;
      const slide = this.slides[i];
      this.slidesEl.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }

    setActive(index) {
      if (index === this.current && this.slides[index]?.classList.contains('is-active')) return;
      this.current = index;
      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        if (i !== index) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) {
          t.setAttribute('aria-current', 'true');
          t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
        } else {
          t.removeAttribute('aria-current');
        }
      });
    }

    openLightbox(src, alt) {
      if (!this.lightbox || !src) return;
      const img = this.lightbox.querySelector('[data-lightbox-img]');
      img.src = src;
      img.alt = alt || '';
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else window.open(src, '_blank');
    }

    /* ---------------- Variantes ---------------- */
    initPicker() {
      if (!this.picker) return;
      this.picker.addEventListener('change', () => this.onOptionChange());
      this.updateAvailability(this.getSelectedOptions());
    }

    getSelectedOptions() {
      return Array.from(this.picker.querySelectorAll('fieldset')).map(
        (fs) => fs.querySelector('input:checked')?.value
      );
    }

    onOptionChange() {
      const selected = this.getSelectedOptions();
      this.picker.querySelectorAll('fieldset').forEach((fs, i) => {
        const label = fs.querySelector('[data-option-selected]');
        if (label) label.textContent = selected[i];
      });
      const variant = this.product.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.updateAvailability(selected);
      this.updateVariant(variant);
    }

    /** Barre les valeurs qui ne mènent à aucune variante disponible. */
    updateAvailability(selected) {
      this.picker.querySelectorAll('fieldset').forEach((fs, i) => {
        fs.querySelectorAll('input').forEach((input) => {
          const available = this.product.variants.some(
            (v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || o === selected[j])
          );
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateVariant(variant) {
      const s = theme.strings || {};
      const buttons = this.root.querySelectorAll('[data-add-to-cart], [data-sticky-button]');

      if (!variant) {
        buttons.forEach((b) => {
          b.disabled = true;
          b.dataset.unavailable = 'true';
          b.querySelector('[data-add-to-cart-text]').textContent = s.unavailable;
        });
        this.setStock(null);
        return;
      }

      // Variante sélectionnée (sélecteur natif = valeur envoyée au panier)
      if (this.select) this.select.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Prix
      const onSale = variant.compare_at_price > variant.price;
      this.root.querySelectorAll('[data-price]').forEach((price) => {
        price.classList.toggle('price--sale', onSale);
        const current = price.querySelector('[data-price-current]');
        const compare = price.querySelector('[data-price-compare]');
        if (current) current.textContent = theme.formatMoney(variant.price);
        if (compare) {
          compare.hidden = !onSale;
          if (onSale) compare.lastChild.textContent = theme.formatMoney(variant.compare_at_price);
        }
      });

      // Badges
      const saleBadge = this.root.querySelector('[data-badge-sale]');
      if (saleBadge) {
        saleBadge.hidden = !(onSale && variant.available);
        const pct = saleBadge.querySelector('[data-badge-discount]');
        if (pct && onSale) pct.textContent = `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
      }
      const lowBadge = this.root.querySelector('[data-badge-low]');
      if (lowBadge) lowBadge.hidden = !this.isLowStock(variant);

      // Boutons
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        b.dataset.unavailable = String(!variant.available);
        const text = b.querySelector('[data-add-to-cart-text]');
        if (text) {
          const short = b.hasAttribute('data-sticky-button');
          text.textContent = variant.available ? (short ? text.dataset.short || text.textContent : s.addToCart) : s.soldOut;
        }
      });

      this.setStock(variant);

      // Média associé à la variante
      if (variant.featured_media_id && this.slides) {
        const slide = this.slides.find((sl) => Number(sl.dataset.mediaId) === variant.featured_media_id);
        if (slide) this.goTo(Number(slide.dataset.index));
      }
    }

    isLowStock(v) {
      return v.available && v.inventory !== null && v.inventory > 0 && v.inventory <= (theme.lowStockThreshold || 5);
    }

    setStock(variant) {
      const el = this.root.querySelector('[data-stock-status]');
      if (!el) return;
      const s = theme.strings || {};
      let state = 'in';
      let text = s.inStock;
      if (!variant) { state = 'out'; text = s.unavailable; }
      else if (!variant.available) { state = 'out'; text = s.soldOut; }
      else if (this.isLowStock(variant)) { state = 'low'; text = (s.lowStock || '').replace('__COUNT__', variant.inventory); }
      el.innerHTML = '';
      const dot = document.createElement('span');
      dot.className = `stock__dot stock__dot--${state}`;
      dot.setAttribute('aria-hidden', 'true');
      el.append(dot, document.createTextNode(text));
    }

    /* ---------------- Barre collante (mobile) ---------------- */
    initSticky() {
      const bar = this.root.querySelector('[data-sticky-atc]');
      const main = this.root.querySelector('[data-add-to-cart]');
      if (!bar || !main || !('IntersectionObserver' in window)) return;

      // Mémorise le libellé court du bouton collant
      const stickyText = bar.querySelector('[data-add-to-cart-text]');
      if (stickyText) stickyText.dataset.short = stickyText.textContent.trim();

      const setVisible = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.inert = !visible;
        document.body.classList.toggle('has-sticky-atc', visible);
      };
      setVisible(false);
      new IntersectionObserver(([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(main);
    }
  }

  const init = (root = document) =>
    root.querySelectorAll('[data-product-section]').forEach((el) => {
      if (!el.dataset.ready) { el.dataset.ready = 'true'; new ProductSection(el); }
    });

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
